import type { ClientProject, ClientProjectSummary } from './api';

const instantPattern = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?Z$/;

function instantKey(value: string) {
  const match = instantPattern.exec(value);
  if (!match) return value;
  return `${match[1]}.${(match[2] ?? '').padEnd(9, '0')}Z`;
}

export function compareProjectUpdatedAt(left: string, right: string) {
  const leftInstant = instantKey(left);
  const rightInstant = instantKey(right);
  if (leftInstant === rightInstant) return 0;
  return leftInstant < rightInstant ? -1 : 1;
}

export function isProjectRevisionOlder(incomingUpdatedAt: string, cachedUpdatedAt: string) {
  return compareProjectUpdatedAt(incomingUpdatedAt, cachedUpdatedAt) < 0;
}

export function projectSummaryFromDetail(project: ClientProject): ClientProjectSummary {
  return {
    id: project.id,
    title: project.title,
    status: project.status,
    sceneCount: project.scenes.length,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
  };
}

export function mergeProjectSummaries(
  existing: readonly ClientProjectSummary[],
  incoming: readonly ClientProjectSummary[],
) {
  const projectsById = new Map(existing.map((project) => [project.id, project]));
  for (const project of incoming) {
    const known = projectsById.get(project.id);
    if (!known || compareProjectUpdatedAt(project.updatedAt, known.updatedAt) >= 0) {
      projectsById.set(project.id, project);
    }
  }
  return [...projectsById.values()].sort((left, right) => {
    const instantOrder = compareProjectUpdatedAt(left.updatedAt, right.updatedAt);
    if (instantOrder !== 0) return -instantOrder;
    if (left.id === right.id) return 0;
    return left.id < right.id ? 1 : -1;
  });
}

export type ProjectListRetryIntent =
  | Readonly<{ kind: 'first-page' }>
  | Readonly<{ kind: 'append'; cursor: string }>;

export function dispatchProjectListRetry<T>(
  intent: ProjectListRetryIntent,
  loadFirstPage: () => T,
  loadAppend: (cursor: string) => T,
) {
  return intent.kind === 'first-page' ? loadFirstPage() : loadAppend(intent.cursor);
}

export function reusableCachedProject(project: ClientProject | null | undefined, requestedId: string) {
  return project?.id === requestedId ? project : undefined;
}
